import { Badge, Switch } from "@/components/ui";
import { capitalize, cn, status } from "@/lib/utils";
import { routes } from "@/routes/utils";
import {
  useTaskTermQuery,
  useToggleTaskTermMutation,
} from "@/services/masterData";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const useTaskTerm = () => {
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const queryClient = useQueryClient();

  const queryParams = {
    ...filterData,
    ...(filterData?.name ? { name: filterData?.name } : {}),
    orderBy: "createdAt",
    orderDirection: "DESC",
    page: pageNumber + 1, // API expects 1-based page numbers
    limit: pageSize,
  };

  const {
    data: response,
    isPending,
    isError,
    error,
  } = useTaskTermQuery(queryParams, {
    onError: (err) => {
      toast.error("Failed to fetch task terms");
    },
  });

  const { mutate: toggleTaskTerm } = useToggleTaskTermMutation({
    onSuccess: () => {
      toast.success("Task Term Status Updated Successfully");
      queryClient.invalidateQueries({ queryKey: ["taskTerm"] });
    },
    onError: () => {
      toast.error("Failed to update status");
    },
  });

  const tableData = response?.data?.[0]?.records || [];
  const totalRecords = response?.data?.[0]?.pagination?.[0]?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "taskTerm",
      header: <div>Task Term</div>,
      cell: (row: any) => (
        <span className="text-primary font-normal cursor-pointer" 
          onClick={()=>onRowClick(row)}
        >
          {String(row?.row?.original?.taskTerm || "")}
        </span>
      ),
      size: 80,
    },
    {
      accessorKey: "days",
      header: <div>Days</div>,
      accessorFn: (row: any) => String(row?.days || ""),
      size: 80,
    },
    {
      accessorKey: "description",
      header: <div>Description</div>,
      accessorFn: (row: any) => String(row?.taskText || ""),
    },
    {
      accessorKey: "createdAt",
      header: <div>Created On</div>,
      accessorFn: (row: any) =>
        moment(row?.createdAt).format("DD-MM-YYYY") || "",
    },
    {
      accessorKey: "isActive",
      header: <div>Status</div>,
      cell: (row: any) => {
        const _status = row.row.original.isActive ? "active" : "inactive";
        return (
          <Badge
            variant="outline"
            className={cn(
              (status as any)[_status]?.color,
              (status as any)[_status]?.bgColor,
              "rounded-[5px] px-4 py-2 border-none"
            )}
          >
            {capitalize(row?.row?.original?.isActive ? "Active" : "Inactive")}
          </Badge>
        );
      },
    },
    {
      accessorKey: "status",
      header: <div>Action</div>,
      cell: (row: any) => (
        <Switch
          checked={row.row.original.isActive}
          className="cursor-pointer"
          onCheckedChange={() => toggleTaskTerm({ id: row.row.original._id })}
        />
      ),
    },
  ];

  const onRowClick = (row: any) => {
    navigate(`${routes.taskTerm}/${row?.row?.original._id}/${routes.edit}`);
  };

  const onAdd = () => {
    navigate(`${routes.taskTerm}/${routes.add}`);
  };

  const onCancelFilter = () => {
    setOpenFilter(false);
  };

  const onFilterBtnClick = () => {
    setOpenFilter(true);
  };

  return {
    columns,
    onAdd,
    onCancelFilter,
    onFilterBtnClick,
    openFilter,
    filterData,
    setFilterData,
    tableData,
    isPending,
    isError,
    pageNumber,
    pageSize,
    totalPages,
    totalRecords,
    setPageNumber,
    setPageSize,
  };
};

export default useTaskTerm;
